import { capitalize } from "lodash";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Button as BootstrapButton,
  Col,
  Container,
  Dropdown,
  Row,
} from "react-bootstrap";
import { useHistory } from "react-router-dom";
import {
  type Case,
  type CaseMetadata,
  type Client,
  type CollectionItem,
  type CreateDiscoveryRequest,
  DocumentStatus,
  DocumentSubType,
  DocumentType,
  type FirmApi,
  ObjectTypeEnum,
  PartyPosition,
  PropoundingParty,
  Respondent,
  type ServiceManner,
} from "@/briefpoint-client";
import Button from "@/components/Button";
import { PopupNavigator } from "@/components/ExternalPartner/AuthPopup/PopupNavigator";
import { FormPreventDefault } from "@/components/Form";
import Loading from "@/components/Loading";
import PdfViewer from "@/components/PdfViewer";
import {
  useCaseApi,
  useClientApi,
  useCommunicationApi,
  useDocumentApi,
  useFirmApi,
  useObjectionApi,
  useUserApi,
} from "@/hooks/useApi";
import { useAuth } from "@/hooks/useAuth";
import type {
  CaseResponse,
  ClientResponse,
  DocumentDetailClient,
  ExternalCaseIEnumerableExternalPartnerResponseResponse,
  ExternalCaseResponse,
  ExternalClientIEnumerableExternalPartnerResponseResponse,
  ExternalClientResponse,
  FirmResponse,
  JurisdictionResponse,
  UserResponse,
  ViewCommunicationResponse,
} from "@/lib/types";
import { getSubTypeText, normalizeError, readParams } from "@/lib/utils";
import SourceDocumentReviewSteps from "./SourceDocumentReviewSteps";
import "react-bootstrap-typeahead/css/Typeahead.css";
import rg4js from "raygun4js";
import { Link } from "react-router-dom";
import { GetExternalPartnerAuthUrl } from "@/components/ExternalPartner/ExternalPartnerConnection";
import Sparkle from "@/components/Sparkle";
import useConfig, { AUTOOBJECT_FF } from "@/hooks/useConfig";
import { GetEnumValues } from "@/hooks/useDocuments";
import { CaliforniaId, FederalId, SEARCH_PARAMS } from "@/lib/constants";
import { isAbortError } from "@/lib/type-guards";
import type { FirmClient } from "@/lib/types";
import { isStringIsNullOrEmpty } from "@/utils";
import {
  externalRequestWrapper,
  handleExternalReqResCodes,
} from "@/utils/ExternalPartner/utils";
import ReviewPageOne from "./ReviewPageOne";
import ReviewPageOnePd from "./ReviewPageOnePd";
import ReviewPageThree from "./ReviewPageThree";
import ReviewPageThreePd from "./ReviewPageThreePd";
import ReviewPageTwo from "./ReviewPageTwo";
import ReviewPageTwoPd from "./ReviewPageTwoPd";
import styles from "./SourceDocumentReview.module.scss";
import {
  createFuzzySearch,
  remapExternalClientList,
  sortClientsBySearchResults,
} from "./utils";

export const blankId = "00000000-0000-0000-0000-000000000000";

export async function checkAndAddJurisdictionToOffice(
  jurisdictionId: string,
  firm: FirmClient | FirmResponse,
  firmApi: FirmApi,
) {
  // check if firm has jurisdictionId in its offices
  if (
    !firm?.offices?.some((o) =>
      o.jurisdictions?.some((j) => j.jurisdictionId === jurisdictionId),
    )
  ) {
    // add jurisdictionId to the firm
    await firmApi.firmUpdateOffice({
      id: firm.id,
      officeId: firm.offices?.[0]?.id ?? "",
      addJxModel: { jurisdictionId: jurisdictionId },
    });
  }
}

export interface DocumentTypeSelection {
  subtype: DocumentSubType;
  number?: number;
}

export const delay = (ms: number) =>
  new Promise((resolve) => setTimeout(resolve, ms));

export default function SourceDocumentReview({
  document,
  updateDocument,
  jurisdictions,
}: {
  document: DocumentDetailClient;
  updateDocument: (document: DocumentDetailClient) => void;
  jurisdictions: JurisdictionResponse[];
}) {
  const { user, setUser, firm } = useAuth();
  if (!user) throw new Error("User is undefined");
  if (!firm) throw new Error("Firm is undefined");
  if (!user.id) throw new Error("User id is undefined");
  if (!user.firmId) throw new Error("User firmId is undefined");
  if (!firm.id) throw new Error("Firm id is undefined");

  const [showEdit, setShowEdit] = useState(!document.caseId);
  const communicationApi = useCommunicationApi();
  // const [setActiveRef, setActiveLineRef, setInformationRef] = usePositionActive();
  const [sections] = useState<string[]>(
    document.info?.documentType === DocumentType.Pleading
      ? ["page1Pd", "page2Pd", "page3Pd"]
      : ["page1", "page2", "page3"],
  );
  const [section, setSection] = useState(
    document.info?.documentType === DocumentType.Pleading ? "page1Pd" : "page1",
  );
  const [judge, setJudge] = useState(document.info?.judge || "");
  // `Case` type is intentional, for creates without clientId, id
  const [_case, setCase] = useState<Case | undefined>();
  const [cacheNewCase, setCacheNewCase] = useState<CaseResponse | undefined>();
  // `Client` type is intentional, for creates without clientId, id
  const [client, setClient] = useState<Client | undefined>();
  // `Case` type is intentional, for creates without clientId, id
  const [cases, setCases] = useState<Case[] | undefined>();
  const [externalCases, setExternalCases] = useState<
    ExternalCaseResponse[] | undefined
  >();
  // `Client` type is intentional, for creates without clientId, id
  const [clients, setClients] = useState<Client[] | undefined>();
  const [externalClients, setExternalClients] = useState<
    ExternalClientResponse[] | undefined
  >();
  // const [filedDate, setFiledDate] = useState(document.info?.filedDate || undefined);
  const [confirmedSections, setConfirmedSections] = useState<string[]>([]);
  const [venue, setVenue] = useState(document.info?.venue || "");
  const [jurisdictionId, setJurisdictionId] = useState(
    document.info?.jurisdiction,
  );
  const [caseNumber, setCaseNumber] = useState(document.info?.caseNumber || "");
  const [caseMetaData, setCaseMetaData] = useState<CaseMetadata | undefined>(
    document.info?.caseMetadata || undefined,
  );
  const [selectedRequestTypes, setSelectedRequestTypes] = useState<
    DocumentTypeSelection[]
  >([
    { subtype: DocumentSubType.RequestsForProduction },
    { subtype: DocumentSubType.RequestsForAdmission },
    { subtype: DocumentSubType.SpecialInterrogatories },
  ]);
  const [generatingRequests, setGeneratingRequests] = useState(false);
  const [documentType] = useState(
    document.info?.documentType || DocumentType.Unknown,
  );
  const [documentSubType, setDocumentSubType] = useState(
    document.info?.documentSubType || DocumentSubType.Unknown,
  );
  const [propoundingParty, setPropoundingParty] = useState(
    document.info?.propoundingParty,
  );
  const [clientIsDefendant, setClientIsDefendant] = useState(
    _case?.clientPosition === PartyPosition.Defendant,
  );
  const [respondingParty, setRespondingParty] = useState(
    document.info?.respondingParty,
  );
  const [propoundingPartyName, setPropoundingPartyName] = useState(() => {
    const documentPropoundName = document.info?.propoundingPartyName ?? "";

    if (documentType === DocumentType.Pleading) {
      const plaintiffs = document.info?.caseMetadata?.plaintiffs;

      // Check to see if this document is part of an existing case, if so, use the current case's propounding party name (this is for those times where a user revisits the first step again)
      if (document.caseId) {
        return documentPropoundName;
      }

      if (
        plaintiffs &&
        plaintiffs.length > 0 &&
        plaintiffs[0] &&
        plaintiffs[0].text
      ) {
        return plaintiffs[0].text;
      }

      return documentPropoundName;
    }
    return documentPropoundName;
  });
  const [respondingPartyName, setRespondingPartyName] = useState(() => {
    const documentRespondName = document.info?.respondingPartyName ?? "";

    if (documentType === DocumentType.Pleading) {
      const defendants = document.info?.caseMetadata?.defendants;

      // Check to see if this document is part of an existing case, if so, use the current case's responding party name (this is for those times where a user revisits the first step again)
      if (document.caseId) {
        return documentRespondName;
      }

      if (
        defendants &&
        defendants.length > 0 &&
        defendants[0] &&
        defendants[0].text
      ) {
        return defendants[0].text;
      }

      return documentRespondName;
    }
    return documentRespondName;
  });
  const [communication, setCommunication] = useState<
    ViewCommunicationResponse[] | undefined
  >();
  const [isCrossComplaint] = useState(document.info?.isCrossComplaint || false);
  const [areThereFuzzyClientResults, setAreThereFuzzyClientResults] =
    useState<boolean>(false);
  const [respondent, setRespondent] = useState(
    document.info?.respondent || Respondent.Respondent,
  );
  const [respondentCustom, setRespondentCustom] = useState(
    document.info?.respondentCustom || formatRespondentCustom(),
  );
  const [setNumber, setSetNumber] = useState(document.info?.setNumber || 1);
  const [saving, setSaving] = useState(false);
  const [autoObject, setAutoObject] = useState(false);
  const [isExternalCasesLoading, setIsExternalCasesLoading] =
    useState<boolean>(false);
  const [startingNumber, setStartingNumber] = useState(
    document.info?.startingNumber || 1,
  );
  const [dateOfService, setDateOfService] = useState<string | undefined>(
    document.info?.dateOfService || undefined,
  );
  const [methodOfService, setMethodOfService] = useState<
    ServiceManner | undefined
  >(document.info?.serviceManner);
  const [dueDate, setDueDate] = useState<string | undefined>(
    document.info?.dueDate || undefined,
  );
  const [formError, setFormError] = useState("");
  const history = useHistory();
  const caseApi = useCaseApi();
  const clientApi = useClientApi();
  const firmApi = useFirmApi();
  const userApi = useUserApi();
  const documentApi = useDocumentApi();
  const objectionApi = useObjectionApi();
  const [config] = useConfig();
  const originalClient = useRef<ClientResponse>(undefined);
  const originalCase = useRef<CaseResponse>(undefined);
  // FOR PLEADINGS
  const [isPleadingDocsPolling, setIsPleadingDocsPolling] =
    useState<boolean>(false);
  const [foundFuzzyPartyId, setFoundFuzzyPartyId] = useState<
    string | undefined
  >();
  const [caseType, setCaseType] = useState<string | null | undefined>(
    document.info?.caseMetadata?.caseType?.id ?? "",
  );
  const [genericInfo, setGenericInfo] = useState<
    { [key: string]: string } | null | undefined
  >(document.info?.caseMetadata?.genericInfo);
  const [pleadClientPos, setPleadClientPos] = useState<
    | {
        name: string | null | undefined;
        clientPos: PropoundingParty | undefined;
      }
    | undefined
  >({
    name: propoundingPartyName ?? undefined,
    clientPos:
      document.info?.propoundingParty === PropoundingParty.Defendant
        ? PropoundingParty.Defendant
        : PropoundingParty.Plaintiff,
  });
  const [timelineDetails, setTimelineDetails] = useState<
    string | null | undefined
  >(document.info?.caseMetadata?.timeline ?? "");
  const [damages, setDamages] = useState<string | null | undefined>(
    document.info?.caseMetadata?.damages || "",
  );
  const [incidentDate, setIncidentDate] = useState<string | null | undefined>(
    document.info?.caseMetadata?.incidentDate ?? "",
  );
  const [incidentDescription, setIncidentDescription] = useState<
    string | null | undefined
  >(document.info?.caseMetadata?.incidentDescription ?? "");
  const [incidentLocation, setIncidentLocation] = useState<
    string | null | undefined
  >(document.info?.caseMetadata?.incidentLocation ?? "");
  const [incidentTime, setIncidentTime] = useState<string | null | undefined>(
    document.info?.caseMetadata?.incidentTime ?? "",
  );
  const [allegations, setAllegations] = useState<Array<CollectionItem> | null>(
    document.info?.caseMetadata?.allegations ?? null,
  );
  const [injuries, setInjuries] = useState<Array<CollectionItem> | null>(
    document.info?.caseMetadata?.injuries ?? null,
  );
  const [causesOfAction, setCausesOfAction] =
    useState<Array<CollectionItem> | null>(
      document.info?.caseMetadata?.causesOfAction ?? [{ number: 1, text: "" }],
    );
  const [agreements, setAgreements] = useState<Array<CollectionItem> | null>(
    document.info?.caseMetadata?.agreements ?? null,
  );
  const [, featureFlags] = useConfig();

  const useAutoObject = featureFlags()[AUTOOBJECT_FF]
    ? featureFlags()[AUTOOBJECT_FF](user, firm)
    : false;

  // Messages for requests errors
  const ERROR_MSG = {
    GENERAL: "An error occurred. Please try again later.",
    EXTERNAL_PARTNER: {
      GENERAL: `There was an error connecting to ${_case?.integration?.partner}. Please try again later.`,
      FAILED_CLIENT_FETCH: `There was an error retrieving external client data from ${_case?.integration?.partner}.`,
      FAILED_CASE_FETCH: `There was an error retrieving external client data from ${_case?.integration?.partner}.`,
      FAILED_CASE_CREATION: "Unable to create a new case.",
      FAILED_CASE_CLIENT_FETCH: `Unable to retrieve external case clients for current case from ${_case?.integration?.partner}.`,
      FAILED_CASE_CLIENT_EXTERNAL: `Unable to create external client for current case from ${_case?.integration?.partner}.`,
      FAILED_CASE_JURISDICTION_VENUE_SET: `Unable to set external case's jurisdiction/venue from ${_case?.integration?.partner}.`,
      USER_CANCELLED: "Authentication cancelled",
    },
    PLEADINGS: {
      FAIL: "There was an error checking the status of generated requests.",
    },
  };

  const completedSteps = new Set(
    confirmedSections.flatMap<number>((_, index) =>
      Object.keys(confirmedSections).length ? [index] : [],
    ),
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: watch setFormError
  const initiateAuth = useCallback(async () => {
    const firmProviders = firm.preferences?.enabledExternalProviders?.values();
    const enabledPartners = firmProviders ? Array.from(firmProviders) : null;

    if (!enabledPartners?.length) {
      return null;
    }

    const partner = enabledPartners[0];
    if (!user.id) throw new Error("User id is undefined");
    const authUrl = GetExternalPartnerAuthUrl(partner, user.id, config);
    if (!authUrl) {
      throw new Error("External partner auth URL is required");
    }
    const popupNavigator = new PopupNavigator();
    const handle = await popupNavigator.prepare({});

    try {
      const result = await handle.navigate({
        url: authUrl,
      });

      const params = readParams(result.url);
      const code = params.get("code");
      if (code) {
        const tokenResult = await userApi.userExternalPartnerAuth({
          userId: user.id,
          code,
          partner: partner,
        });
        if (tokenResult.responseCode !== 200) {
          throw new Error(
            `Unable to authenticate to ${partner}. Status Code: ${tokenResult.responseCode}`,
          );
        }

        setUser((u) => {
          const newUser = { ...u } as UserResponse;
          newUser.externalConnection = { partner: partner, isActive: true };
          return newUser;
        });

        // If the user reAuthenticates from a 401 response, fetch external cases to display in the selectable case list
        if (!externalCases) {
          if (!user.firmId) throw new Error("User firmId is undefined");
          // const res = (await caseApi.caseGetExternal({
          //   firmId: user.firmId,
          // })) as ExternalCaseIEnumerableExternalPartnerResponseResponse;
          const res = {
            responseCode: 200,
            data: [],
          };

          if (res) {
            if (res.responseCode === 200) {
              setExternalCases(res.data ?? undefined);
            } else {
              setFormError(ERROR_MSG.GENERAL);
              rg4js("send", {
                error: normalizeError(res),
              });
            }
          }
        }
      } else {
        setSaving(false);
        throw new Error(`Unexpected response from auth: ${params}`);
      }
      setSaving(false);
    } catch (_e) {
      let message = "";
      if (typeof _e === "string") {
        message = _e;
      } else if (_e instanceof Error) {
        message = _e.message;
      }

      if (message.includes("closed by user")) {
        setFormError(ERROR_MSG.EXTERNAL_PARTNER.USER_CANCELLED);
        throw new Error(ERROR_MSG.EXTERNAL_PARTNER.USER_CANCELLED);
      }
      setSaving(false);
    }
  }, [
    user,
    firm,
    caseApi,
    userApi,
    externalCases,
    setUser,
    setFormError,
    ERROR_MSG.GENERAL,
    ERROR_MSG.EXTERNAL_PARTNER.FAILED_CASE_FETCH,
    ERROR_MSG.EXTERNAL_PARTNER.USER_CANCELLED,
    config,
  ]);

  const loadCommunication = async (id: string) => {
    const communications =
      (await communicationApi.communicationGetCommunicationsForObject({
        objectId: id,
        type: ObjectTypeEnum.Document,
      })) as ViewCommunicationResponse[];
    setCommunication(communications);
  };

  // Update caseType when _case is loaded, but only if caseType wasn't already set from document
  // biome-ignore lint/correctness/useExhaustiveDependencies: ignore section and document.info?.documentType
  useEffect(() => {
    if (
      document.info?.documentType !== DocumentType.Pleading &&
      section !== "page1Pd"
    )
      return;
    const documentCaseType = document.info?.caseMetadata?.caseType?.id;
    if (!documentCaseType && _case?.caseType) {
      setCaseType(_case.caseType);
    }
  }, [_case, document.info?.caseMetadata?.caseType?.id]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: ignore loadCommunication
  useEffect(() => {
    async function load() {
      if (user?.firmId && document.caseId) {
        if (document.clientId) {
          const [docClient, docCase] = await Promise.all([
            (await clientApi.clientGet({
              firmId: user.firmId,
              id: document.clientId,
            })) as ClientResponse,
            document.caseId
              ? ((await caseApi.caseGet({
                  firmId: user.firmId,
                  id: document.caseId,
                })) as CaseResponse)
              : Promise.resolve(),
          ]);
          originalClient.current = docClient;
          setClient(docClient);

          if (docCase) {
            originalCase.current = docCase;
            setCase(docCase);
            if (!document?.info?.venue && docCase.venue) {
              setVenue(docCase.venue);
            }
          } else {
            setPleadClientPos((prevState) => ({
              clientPos: prevState?.clientPos,
              name: docClient.name ?? "",
            }));
          }
        }
      }
    }
    load();
    loadCommunication(document.id);
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [caseApi, clientApi, user, document, jurisdictions]);

  const handleExternalCaseSet = useCallback(
    (
      fetchedExternalCases: ExternalCaseIEnumerableExternalPartnerResponseResponse,
    ) => {
      if (fetchedExternalCases) {
        if (fetchedExternalCases.responseCode === 200) {
          setExternalCases(fetchedExternalCases.data ?? []);
        } else {
          if (fetchedExternalCases.responseCode === 401) {
            // initiate re-auth
            initiateAuth();
          } else {
            // show some sort of alert
            setFormError(ERROR_MSG.GENERAL);
            rg4js("send", {
              error: normalizeError(fetchedExternalCases),
            });
          }
        }
        setIsExternalCasesLoading(false);
      }
    },
    [ERROR_MSG.GENERAL, initiateAuth],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: watch firm
  useEffect(() => {
    async function load() {
      if (user?.firmId && !cases && !clients) {
        const [allCases, allClients] = await Promise.all([
          (await firmApi.firmGetFirmCases({
            firmId: user.firmId,
          })) as CaseResponse[],
          (await clientApi.clientGetAll({
            firmId: user.firmId,
          })) as ClientResponse[],
        ]);

        if (
          document.caseId ||
          !document.info?.plaintiffs ||
          !document.info?.defendants
        ) {
          let st: string;
          const docRespondingName = document.info?.respondingPartyName ?? "";
          const docPropoundingName = document.info?.propoundingPartyName ?? "";
          const docCaseNum = document.info?.caseNumber ?? "";

          if (
            document.info?.respondingPartyName &&
            document.info?.propoundingPartyName
          ) {
            st = `${docRespondingName?.toUpperCase()} vs ${docPropoundingName?.toUpperCase()}`;
          } else {
            st = "";
          }

          if (
            !document.info?.plaintiffs &&
            !document.info?.defendants &&
            document.info?.respondingPartyName &&
            document.info.propoundingPartyName &&
            st !== ""
          ) {
            if (!document.caseId) {
              if (!allCases.find((c) => c.shortTitle === st)) {
                const c = {
                  shortTitle: st,
                  id: undefined,
                  clientId: undefined,
                };
                setCases([...allCases, c]);
                setCase(c);
              } else {
                // Match an existing case name, if case number and document's case number matches, match the whole case, otherwise just the name.
                const matchedCaseByName = allCases.find(
                  (c) => c.shortTitle?.toLowerCase() === st.toLowerCase(),
                );

                if (matchedCaseByName && !document.caseId) {
                  setCase((prevState) => {
                    if (matchedCaseByName.caseNumber === docCaseNum) {
                      return matchedCaseByName;
                    }
                    return !prevState?.shortTitle
                      ? {
                          shortTitle: matchedCaseByName.shortTitle ?? "",
                          id: matchedCaseByName.id,
                          clientId: matchedCaseByName.clientId,
                        }
                      : undefined;
                  });
                }
                setCases(allCases);
              }
            }
          } else {
            setCases(allCases);
          }
        } else {
          let st: string;
          const docPlaintiff = document.info.plaintiffs.split(",")[0];
          const docDefendant = document.info.defendants.split(",")[0];

          if (document.info?.documentType === DocumentType.Pleading) {
            if (document.info.caseMetadata) {
              st = `${document.info.caseMetadata.plaintiffs?.[0].text ?? docPlaintiff} vs ${document.info.caseMetadata.defendants?.[0].text ?? docDefendant}`;
            } else {
              st = `${docPlaintiff} vs ${docDefendant}`;
            }
          } else {
            st = `${docPlaintiff} vs ${docDefendant}`;
          }

          if (!allCases.find((c) => c.shortTitle === st)) {
            const c = { shortTitle: st };
            setCases([...allCases, c]);
            setCase(c);
          } else {
            setCases(allCases);
          }
        }

        // Exclude common words from fuzzy client search
        const excludeFuzzyClientQuery: string[] = [
          "inc",
          "INC",
          "llc",
          "LLC",
          "the",
          "THE",
          "corp",
          "CORP",
        ];
        const caseMetadataPtiff = document.info?.caseMetadata?.plaintiffs;
        const nameToUse =
          document.info?.documentType === DocumentType.Pleading
            ? caseMetadataPtiff &&
              caseMetadataPtiff.length > 0 &&
              caseMetadataPtiff[0] &&
              caseMetadataPtiff[0].text
              ? caseMetadataPtiff[0].text
              : document.info?.propoundingPartyName
            : document.info?.respondingPartyName;

        const fuzzyResults = createFuzzySearch(
          allClients,
          {
            // Prioritize matching the name values over aliases
            keys: [
              { name: "name", weight: 2 },
              { name: "aliases", weight: 1 },
            ],
            includeScore: true,
            threshold: 0.4,
          },
          excludeFuzzyClientQuery,
          nameToUse,
        );
        const sortedFuzzySearch = sortClientsBySearchResults(
          allClients,
          fuzzyResults,
        );

        const pickNameToUse = () => {
          if (fuzzyResults && fuzzyResults.length > 0) {
            return fuzzyResults[0].item.name ?? nameToUse;
          }
          return nameToUse;
        };

        if (document.clientId || !nameToUse) {
          setClients(allClients);
        } else {
          if (
            !allClients.find(
              (c) => c.name?.toLowerCase() === pickNameToUse()?.toLowerCase(),
            )
          ) {
            const c = { name: nameToUse };
            setClients([...allClients, c]);
            setClient(c);
            setPleadClientPos((prevState) => ({
              clientPos: prevState?.clientPos,
              name: c.name,
            }));
          } else {
            const matchedClientByName = allClients.find(
              (c) => c.name?.toLowerCase() === pickNameToUse()?.toLowerCase(),
            );
            if (matchedClientByName?.id) {
              setClient((prevState) => {
                return !prevState?.name ? matchedClientByName : prevState;
              });
            }
            setAreThereFuzzyClientResults(true);
            setClients(sortedFuzzySearch);
          }
        }

        if (!document.clientId && document.caseId) {
          const _case = allCases.find((x) => x.id === document.caseId);
          if (_case) {
            setCase(_case);
          }
        }

        if (
          !document.clientId &&
          !document.caseId &&
          document.info?.caseNumber
        ) {
          const _case = allCases.find(
            (x) => x.caseNumber === document.info?.caseNumber,
          );

          if (_case) {
            setCase(_case);
            setShowEdit(false);
            const client = allClients.find((x) => x.id === _case.clientId);
            setClient(client);
          }
        }
      }
    }
    load();
  }, [
    clientApi,
    firmApi.firmGetFirmCases,
    user,
    firm,
    cases,
    clients,
    document,
  ]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: watch cases, clients
  useEffect(() => {
    async function load() {
      if (user?.firmId) {
        // Update the externalCases after a successful connection w/ a partner
        const tempExternalCases = (await caseApi.caseGetExternal({
          firmId: user.firmId,
        })) as ExternalCaseIEnumerableExternalPartnerResponseResponse;
        handleExternalCaseSet(tempExternalCases);
      }
    }

    load();
  }, [caseApi, cases, clients, user]);

  useEffect(() => {
    let isMounted = true;

    const abortController = new AbortController();

    async function pollDocuments() {
      while (isMounted && isPleadingDocsPolling) {
        try {
          await delay(2000);
          const results = await documentApi.documentGetList(
            { parentDocumentId: document.id, source: 9 },
            { signal: abortController.signal },
          );
          const ready = results.find(
            (x) => x.status === DocumentStatus.Processed,
          );
          if (ready && isMounted) {
            history.push(`/wizard/propound/${document.id}`);
            setIsPleadingDocsPolling(false);
          }
        } catch (error: unknown) {
          if (isAbortError(error)) {
            // Abort polling when moving away to a different view
            console.error(error);
          } else {
            rg4js("send", {
              error: normalizeError(error),
            });
            throw new Error(ERROR_MSG.PLEADINGS.FAIL);
          }
          setIsPleadingDocsPolling(false);
        }
      }
    }

    if (generatingRequests) {
      setIsPleadingDocsPolling(true);
      pollDocuments();
    }

    return () => {
      isMounted = false;
      abortController.abort();
      setIsPleadingDocsPolling(false);
    };
  }, [
    generatingRequests,
    documentApi,
    document.id,
    history,
    isPleadingDocsPolling,
    ERROR_MSG.PLEADINGS.FAIL,
  ]);

  const handleFilterChange = useCallback(
    async (filter?: string) => {
      if (user?.firmId && user.externalConnection?.isActive) {
        setIsExternalCasesLoading(true);
        const getExternalCases = (await caseApi.caseGetExternal({
          firmId: user.firmId,
          filter,
        })) as ExternalCaseIEnumerableExternalPartnerResponseResponse;
        handleExternalCaseSet(getExternalCases);
        setIsExternalCasesLoading(false);
      }
    },
    [caseApi, handleExternalCaseSet, user],
  );

  function updateRespondent(respondent: Respondent) {
    if (respondent === Respondent.Custom) {
      setRespondentCustom(
        document.info?.respondentCustom || formatRespondentCustom(),
      );
    }

    setRespondent(respondent);
  }

  function formatRespondentCustom() {
    const excludedList = ["et", "al", "and", "with"];

    return (
      client?.respondentName ||
      client?.name ||
      (document.info?.respondingPartyName || "")
        .split(" ")
        .map((value) => {
          if (excludedList.includes(value)) {
            return value.toLowerCase();
          }
          return capitalize(value);
        })
        .join(" ")
    );
  }

  // function jumpTo(stepIndex: number) {
  //   if (documentType === DocumentType.Pleading) {
  //     if (confirmedSections.length > stepIndex) {
  //       if (stepIndex === 0) {
  //         setSection("page1Pd");
  //         setConfirmedSections([]);
  //       } else if (stepIndex === 1) {
  //         setSection("page2Pd");
  //         setConfirmedSections(["page1Pd"]);
  //       } else if (stepIndex === 2) {
  //         setSection("page3Pd");
  //         setConfirmedSections(["page1Pd", "page2Pd"]);
  //       }
  //     }
  //   } else {
  //     if (confirmedSections.length > stepIndex) {
  //       if (stepIndex === 0) {
  //         setSection("page1");
  //         setConfirmedSections([]);
  //       } else if (stepIndex === 1) {
  //         setSection("page2");
  //         setConfirmedSections(["page1"]);
  //       } else if (stepIndex === 2) {
  //         setSection("page3");
  //         setConfirmedSections(["page1", "page2"]);
  //       }
  //     }
  //   }
  // }

  function cancelShowEdit() {
    // Use cached case/client data in case the user removes case info on step 1
    if (originalClient.current) {
      setClient(originalClient.current);
      setCase(originalCase.current);
    }
    setShowEdit(false);
  }

  /*
   * TODO: refactor.
   *
   * A simpler and type-safe way to handle this (or any wizard)
   * is a parent component that shows a different component based
   * on a url param that is an enum of step names.
   * * Step names and components should be descriptive rather
   *   than numbered, because there is always a chance that
   *   step 3.5 will be added.
   * * Ideally the child components are simple forms. They don't
   *   need to know they are part of a wizard.
   * * If you can't allow users to advance without filling out a step,
   *   use the parent Wizard component to enforce it.
   *
   * Example:
   * =========
   *
   * enum Step {
   *   firstName: "firstName",
   *   lastName: "lastName",
   *   email: "email",
   * }
   *
   * export function Wizard = ({ step }: { step: Step }) => {
   *   const params = useParams<{ step: Step }>();
   *   switch (step) {
   *     case Step.firstName:
   *       return <FirstNameForm nextStep={Step.lastName} />;
   *     case Step.lastName:
   *       return <LastNameForm nextStep={Step.email} prevStep={Step.firstName} />;
   *     case Step.email:
   *       return <EmailForm nextStep={Step.submit} prevStep={Step.lastName} />;
   *   }
   * }
   *
   */
  const SectionComponent = {
    page1: ReviewPageOne,
    page1Pd: ReviewPageOnePd,
    page2: ReviewPageTwo,
    page2Pd: ReviewPageTwoPd,
    page3: ReviewPageThree,
    page3Pd: ReviewPageThreePd,
  }[section] as React.ComponentType<Record<string, unknown>>;

  async function handleUpdateDocument(autoObjectAfter = false) {
    setFormError("");
    let success = true;
    try {
      //sometimes updated info takes a bit to propagate, if we don't get a success, wait 1 second and try again
      await updateDocumentAndRetryOnFail();

      if (autoObjectAfter) {
        const documentId = document.id;
        // fire auto-objections in background so user can navigate immediately; wizard will poll document when AUTO_OBJECT_PENDING param is in URL
        documentApi
          .documentSuggestObjectionsForAll({
            documentId,
          })
          .catch((err) => {
            rg4js("send", { error: normalizeError(err) });
          });
      }
    } catch {
      setFormError(ERROR_MSG.GENERAL);
      success = false;
    }
    setSaving(false);
    return success;
  }

  async function updateDocumentAndRetryOnFail(
    maxRetries = 3,
    baseDelay = 1000,
  ) {
    let lastError: unknown;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        return await updateDocument(document);
      } catch (error) {
        lastError = error;
        if (attempt === maxRetries) {
          break;
        }
        const delay = baseDelay * 2 ** attempt;
        const jitter = Math.random() * 1000;
        await new Promise((resolve) => setTimeout(resolve, delay + jitter));
      }
    }
    throw lastError;
  }

  async function sdrUiFlow(autoObjectAfter = false) {
    if (section === "page1") {
      if (!venue) {
        if (!document.info?.venue || document.info.venue === blankId) {
          setFormError("Please select a Venue");
          setSaving(false);
          return;
        }
        setVenue(document.info.venue);
      }

      if (!_case) {
        setFormError("Please select or create a Case");
        setSaving(false);
        return;
      }

      let tempCase = _case;
      const tempClient = client;
      // Cache the results of new a new case/external case to avoid case creation duplication
      // fetching external clients from smokeball times out
      let newCase = cacheNewCase;
      // Check to see if venue is blank/empty, use the document extracted venue, otherwise use the selected valid venue
      const determineVenue = venue === blankId ? document.info?.venue : venue;

      function exClientFetchSuccess(
        res: ExternalClientIEnumerableExternalPartnerResponseResponse,
      ) {
        const remappedData = remapExternalClientList(res) ?? [];

        if (remappedData.length === 0) {
          setExternalClients(undefined);
          return;
        }

        setExternalClients(remappedData);

        if (!client?.id) {
          const matchedClient: Client =
            remappedData.find(
              (val) => val.name === document.info?.respondingPartyName,
            ) ?? remappedData[0];
          if (
            matchedClient &&
            matchedClient.id === "" &&
            user?.externalConnection?.isActive
          ) {
            // Ensure the id is undefined if its just an empty string, otherwise the backend will throw an error
            // when adding the client on step three
            matchedClient.id = undefined;
          }
          setClient(matchedClient);
          setRespondingPartyName(matchedClient.name || "");
        }
      }

      function exClientFetchError(
        res: ExternalClientIEnumerableExternalPartnerResponseResponse,
      ) {
        setFormError(ERROR_MSG.EXTERNAL_PARTNER.GENERAL);
        if (tempClient) {
          tempClient.name = "";
        }
        rg4js("send", {
          error: normalizeError(res),
        });
        setSaving(false);
        throw new Error(ERROR_MSG.EXTERNAL_PARTNER.FAILED_CASE_CLIENT_FETCH);
      }

      // Creating a new case
      async function fetchExternalClients() {
        if (!user?.firmId) throw new Error("user.firmId is undefined");
        if (!tempCase.integration?.identifier)
          throw new Error("tempCase.integration.identifier is undefined");

        const res = (await externalRequestWrapper(
          clientApi.clientGetExternal({
            firmId: user.firmId,
            externalCaseId: tempCase.integration.identifier,
          }),
          3,
        )) as ExternalClientIEnumerableExternalPartnerResponseResponse;
        handleExternalReqResCodes(
          res,
          exClientFetchSuccess,
          exClientFetchError,
          initiateAuth,
        );
      }

      if (!_case?.id) {
        tempCase.jurisdiction = jurisdictionId || undefined;
        tempCase.venue = determineVenue || undefined;
        tempCase.caseNumber = caseNumber;
        tempCase.judge = judge;
        tempCase.title ??= tempCase.shortTitle;
        tempCase.plaintiffs = document.info?.plaintiffs;
        tempCase.defendants = document.info?.defendants;

        try {
          if (!user?.firmId) throw new Error("user.firmId is undefined");

          // Check if we have a cached new case in case external client retrieval failed and the user is re-submitting...
          if (!cacheNewCase) {
            newCase = (await caseApi.casePost({
              firmId: user.firmId,
              _case: tempCase,
            })) as CaseResponse;
            setCacheNewCase(newCase);
          }
          // Create a new external case
          if (newCase && tempCase.integration?.identifier) {
            if (!cacheNewCase) {
              await caseApi.caseExternalIntegration({
                firmId: user.firmId,
                id: newCase?.id,
                externalIntegration: tempCase.integration,
              });
            }
            newCase.integration = tempCase.integration;
            // Get external clients
            try {
              await fetchExternalClients();
            } catch (error) {
              setFormError(ERROR_MSG.EXTERNAL_PARTNER.GENERAL);
              rg4js("send", {
                error: normalizeError(error),
              });
              setSaving(false);
              return;
            }
          }

          if (!newCase) throw new Error("newCase is undefined");

          tempCase = newCase;

          document.clientId = null;
        } catch (error) {
          setFormError(ERROR_MSG.GENERAL);
          rg4js("send", {
            error: normalizeError(error),
          });
          setSaving(false);
          return;
        }
      } else {
        // If the current case already exists
        if (_case.integration?.identifier && user?.firmId) {
          try {
            if (
              _case.jurisdiction === blankId ||
              _case.venue === blankId ||
              jurisdictionId !== _case.jurisdiction
            ) {
              if (!jurisdictionId)
                throw new Error("jurisdictionId is undefined");
              if (!determineVenue)
                throw new Error("determineVenue is undefined");

              tempCase.jurisdiction = jurisdictionId;
              tempCase.venue = determineVenue;

              try {
                if (!tempCase.id) throw new Error("tempCase.id is undefined");

                await caseApi.casePut({
                  firmId: user.firmId,
                  id: tempCase.id,
                  _case: tempCase,
                });
              } catch (error) {
                setFormError(ERROR_MSG.GENERAL);
                rg4js("send", {
                  error: normalizeError(error),
                });
                setSaving(false);
              }
            }
            const res = (await externalRequestWrapper(
              clientApi.clientGetExternal({
                firmId: user.firmId,
                externalCaseId: tempCase.integration?.identifier || undefined,
              }),
              3,
            )) as ExternalClientIEnumerableExternalPartnerResponseResponse;
            handleExternalReqResCodes(
              res,
              exClientFetchSuccess,
              exClientFetchError,
              initiateAuth,
            );
          } catch (error) {
            setFormError(ERROR_MSG.GENERAL);
            rg4js("send", {
              error: normalizeError(error),
            });
            setSaving(false);
            return;
          }
        }

        if ((!_case.caseNumber && caseNumber) || (!_case.judge && judge)) {
          if (isStringIsNullOrEmpty(tempCase.caseNumber)) {
            tempCase.caseNumber = caseNumber;
          }

          if (isStringIsNullOrEmpty(tempCase.judge)) {
            tempCase.judge = judge;
          }
          try {
            if (!user?.firmId) throw new Error("user.firmId is undefined");
            if (!tempCase.id) throw new Error("tempCase.id is undefined");

            await caseApi.casePut({
              firmId: user.firmId,
              id: tempCase.id,
              _case: tempCase,
            });
          } catch {
            setFormError(ERROR_MSG.GENERAL);
            setSaving(false);
            return;
          }
        }
        // Allows the document's case to be changed on step 1 to update the document's client ID for that case. Keep this in!
        document.clientId = tempCase.clientId;
      }

      if (!tempCase.id) throw new Error("tempCase.id is undefined");
      document.caseId = tempCase.id;

      if (document.info) {
        document.info.venue = determineVenue;
        document.info.jurisdiction = jurisdictionId;
      }
      if (await handleUpdateDocument()) {
        setCase(tempCase);
        setSection(
          documentType === DocumentType.Pleading ? "page2Pd" : "page2",
        );
        setConfirmedSections(["page1"]);
      }

      if (jurisdictionId && firm && firmApi) {
        //if user has selected federal and they don't have it in their office add it to the firm
        //in the future we may want to do this for other jurisdictions as well
        await checkAndAddJurisdictionToOffice(jurisdictionId, firm, firmApi);
      }
    } else if (section === "page2") {
      const subTypeChanged = document.info?.documentSubType !== documentSubType;
      if (document.info) {
        document.info.documentSubType = documentSubType;
        document.info.setNumber = setNumber;
        document.info.startingNumber = startingNumber;
        document.info.dateOfService =
          dateOfService?.length === 0 ? null : dateOfService;
        document.info.serviceManner = methodOfService;
        document.info.dueDate = dueDate;
      }

      if (await handleUpdateDocument()) {
        // If they changed the subtype, send them back to document list for re-parsing
        if (subTypeChanged) {
          setSaving(true);

          /* allow a second for processing to start back up before redirecting */
          await delay(1000);
          setSaving(false);
          history.push("/documents");
        } else {
          setSection("page3");
          setConfirmedSections(["page1", "page2"]);
        }
        // }
      }
    } else if (section === "type") {
      if (document.info) {
        document.info.documentType = documentType;
      }
      if (documentType === DocumentType.Pleading) {
        document.status = DocumentStatus.InfoConfirmed;
        if (await handleUpdateDocument()) {
          history.push(`/wizard/${document.id}`);
        }
      } else {
        if (await handleUpdateDocument()) {
          setSection("page3");
          // setConfirmedSections(['page1', 'page2', 'type']);
          setConfirmedSections(["page1", "page2", "page3"]);
        }
      }
    } else {
      if (!client) {
        setFormError("Please select or create a Client");
        setSaving(false);
        return;
      }

      if (propoundingParty === PropoundingParty.Unknown) {
        setFormError("Please select the Propounding Party Type");
        setSaving(false);
        return;
      }

      if (respondingParty === PropoundingParty.Unknown) {
        setFormError("Please select the Responding Party Type");
        setSaving(false);
        return;
      }

      let tempClient = client;
      const tempCase = _case;
      if (!tempCase) return;

      if (tempCase.usePetitionerAndRespondent !== undefined) {
        if (!user?.firmId) throw new Error("user.firmId is undefined");
        if (!tempCase.id) throw new Error("tempCase.id is undefined");

        await caseApi.casePut({
          firmId: user.firmId,
          id: tempCase.id,
          _case: tempCase,
        });
      }

      // Creating a new client
      if (!client?.id) {
        if (
          client &&
          document.info?.respondingPartyName &&
          client.name !== document.info?.respondingPartyName
        ) {
          client.aliases = document.info?.respondingPartyName;
        }

        try {
          if (!user?.firmId) throw new Error("user.firmId is undefined");

          const newClient = (await clientApi.clientPost({
            firmId: user.firmId,
            client: client,
          })) as ClientResponse;
          tempClient = newClient;
        } catch {
          setFormError(ERROR_MSG.GENERAL);
          setSaving(false);
          return;
        }

        if (client?.integration?.identifier) {
          try {
            if (!user?.firmId) throw new Error("user.firmId is undefined");
            if (!tempClient.id) throw new Error("tempClient.id is undefined");

            await clientApi.clientExternalIntegration({
              firmId: user.firmId,
              id: tempClient.id,
              externalIntegration: tempClient?.integration,
            });
          } catch (error) {
            setFormError(ERROR_MSG.GENERAL);
            setSaving(false);
            rg4js("send", {
              error: normalizeError(error),
            });
            throw new Error(
              ERROR_MSG.EXTERNAL_PARTNER.FAILED_CASE_CLIENT_EXTERNAL,
            );
          }
        }
      }

      //update document client id if it doesn't match
      if (!document.clientId || tempClient.id !== document.clientId) {
        document.clientId = tempClient.id;
        if (tempCase) {
          tempCase.clientId = tempCase.clientId || tempClient.id;
        }
      }

      // add client to case
      try {
        if (!user?.firmId) throw new Error("user.firmId is undefined");
        if (!tempCase.id) throw new Error("tempCase.id is undefined");
        if (!tempClient.id) throw new Error("tempClient.id is undefined");

        await caseApi.caseAddClient({
          firmId: user.firmId,
          id: tempCase.id,
          body: tempClient.id,
        });
      } catch {
        setFormError(ERROR_MSG.GENERAL);
        setSaving(false);
        return;
      }

      if (document.info) {
        document.info.documentSubType = documentSubType;
        document.info.propoundingParty =
          propoundingParty || PropoundingParty.Unknown;
        document.info.respondingParty = respondingParty;
        document.info.propoundingPartyName = propoundingPartyName;
        document.info.isCrossComplaint = isCrossComplaint;
        document.info.respondent = respondent;
        document.info.respondingPartyName = respondingPartyName;
        document.info.respondentCustom =
          respondent === Respondent.Custom ? respondentCustom : "";
      }

      // If they've changed the respondentCustom and the Client doesn't already have a respondent name, add it automatically
      tempClient.respondentName = document.info?.respondentCustom;
      try {
        await clientApi.clientPut({
          firmId: user.firmId,
          id: tempClient.id,
          client: tempClient,
        });
      } catch {
        setFormError(ERROR_MSG.GENERAL);
        setSaving(false);
        return;
      }

      if (
        (!tempCase.clientPosition ||
          tempCase.clientPosition === PartyPosition.Unknown) &&
        document.info?.respondingParty
      ) {
        // need to do this jazz because UserService enums are being returned as strings not numbers like the DocumentService,
        // probably need to change UserService to behave like DocumentService but there are too many cases to handle right now
        tempCase.clientPosition = GetEnumValues(
          document.info.respondingParty,
        ).join(", ") as PartyPosition;
        try {
          if (!user?.firmId) throw new Error("user.firmId is undefined");

          await caseApi.casePut({
            firmId: user.firmId,
            id: tempCase.id,
            _case: tempCase,
          });
        } catch {
          setFormError(ERROR_MSG.GENERAL);
          setSaving(false);
          return;
        }
      }

      if (!tempCase?.otherParties?.length && document.info?.propoundingParty) {
        try {
          if (!user?.firmId) throw new Error("user.firmId is undefined");

          await caseApi.caseCreateParty({
            firmId: user.firmId,
            id: tempCase.id,
            party: {
              name: propoundingPartyName,
              position: GetEnumValues(document.info?.propoundingParty).join(
                ", ",
              ) as PartyPosition,
            },
          });
        } catch {
          setFormError(ERROR_MSG.GENERAL);
          setSaving(false);
          return;
        }
      }

      document.status = DocumentStatus.InfoConfirmed;
      if (await handleUpdateDocument(autoObjectAfter)) {
        setCase(tempCase);
        setClient(tempClient);
        history.push(
          `/wizard/${document.id}${autoObjectAfter ? `?${SEARCH_PARAMS.AUTO_OBJECT_PENDING}=1` : ""}`,
        );
      }
    }
  }

  async function sdrPleadingUiFlow(documentSubTypeTemplate?: DocumentSubType) {
    if (!user?.firmId) throw new Error("user.firmId is undefined");

    if (section === "page1Pd") {
      if (!venue) {
        if (!document.info?.venue || document.info.venue === blankId) {
          setFormError("Please select a Venue");
          setSaving(false);
          return;
        }
        setVenue(document.info.venue);
      }

      if (!caseType) {
        setFormError("Please select a Case Type.");
        setSaving(false);
        return;
      }

      if (!_case) {
        setFormError("Please select or create a Case");
        setSaving(false);
        return;
      }

      if (!propoundingPartyName || !client || !pleadClientPos?.name) {
        setFormError("Please select a Propounding Party");
        setSaving(false);
        return;
      }

      if (!respondingPartyName) {
        setFormError("Please select a Responding Party");
        setSaving(false);
        return;
      }

      let tempCase = _case;

      let tempClient = client;
      // Cache the results of new a new case/external case to avoid case creation duplication
      // fetching external clients from smokeball times out
      let newCase = cacheNewCase;
      // Check to see if venue is blank/empty, use the document extracted venue, otherwise use the selected valid venue
      const determineVenue = venue === blankId ? document.info?.venue : venue;
      const setDefendantName =
        pleadClientPos?.clientPos === PropoundingParty.Defendant
          ? propoundingPartyName
          : respondingPartyName;
      const setPlaintiffName =
        pleadClientPos?.clientPos === PropoundingParty.Defendant
          ? respondingPartyName
          : propoundingPartyName;

      const fuzzyOtherPartyResults = createFuzzySearch(
        _case?.otherParties ?? [],
        { keys: ["name"], includeScore: true, threshold: 0.5 },
        [],
        respondingPartyName,
      );

      document.info = {
        ...document.info,
        documentType,
        documentSubType,
        propoundingParty: propoundingParty || PropoundingParty.Unknown,
        respondingParty,
        propoundingPartyName,
        isCrossComplaint,
        respondent,
        respondingPartyName,
        respondentCustom:
          respondent === Respondent.Custom ? respondentCustom : "",
        defendants: setDefendantName,
        plaintiffs: setPlaintiffName,
      };

      tempCase = {
        ...tempCase,
        defendants: setDefendantName,
        plaintiffs: setPlaintiffName,
        clientPosition: clientIsDefendant
          ? PartyPosition.Defendant
          : PartyPosition.Plaintiff,
      };

      function exClientFetchSuccess(
        res: ExternalClientIEnumerableExternalPartnerResponseResponse,
      ) {
        const remappedData = remapExternalClientList(res);

        const matchedNameIndex = remappedData?.findIndex(
          (val) => val.name === document.info?.respondingPartyName,
        );
        setExternalClients(remappedData ?? undefined);

        if (
          remappedData &&
          remappedData.length > 0 &&
          matchedNameIndex !== undefined
        ) {
          setClient(
            matchedNameIndex !== -1
              ? remappedData[matchedNameIndex]
              : remappedData[0],
          );
        }
      }

      function exClientFetchError(
        res: ExternalClientIEnumerableExternalPartnerResponseResponse,
      ) {
        setFormError(ERROR_MSG.EXTERNAL_PARTNER.GENERAL);
        tempClient.name = "";
        rg4js("send", {
          error: normalizeError(res),
        });
        setSaving(false);
        throw new Error(ERROR_MSG.EXTERNAL_PARTNER.FAILED_CASE_CLIENT_FETCH);
      }

      // Creating a new case
      async function fetchExternalClients() {
        if (!user?.firmId) throw new Error("user.firmId is undefined");

        const res = (await externalRequestWrapper(
          clientApi.clientGetExternal({
            firmId: user.firmId,
            externalCaseId: tempCase.integration?.identifier || undefined,
          }),
          3,
        )) as ExternalClientIEnumerableExternalPartnerResponseResponse;
        handleExternalReqResCodes(
          res,
          exClientFetchSuccess,
          exClientFetchError,
          initiateAuth,
        );
      }

      if (!_case?.id) {
        tempCase.jurisdiction = jurisdictionId ?? undefined;
        tempCase.venue = determineVenue ?? undefined;
        tempCase.caseNumber = caseNumber;
        tempCase.judge = judge;
        tempCase.caseType = caseType;
        tempCase.title ??= tempCase.shortTitle;

        try {
          // Check if we have a cached new case in case external client retrieval failed and the user is re-submitting...
          if (!cacheNewCase) {
            newCase = (await caseApi.casePost({
              firmId: user.firmId,
              _case: tempCase,
            })) as CaseResponse;
            setCacheNewCase(newCase);
          }
          // Create a new external case
          if (tempCase.integration?.identifier) {
            if (!cacheNewCase) {
              if (!newCase?.id) {
                throw new Error("newCase.id is undefined");
              }

              await caseApi.caseExternalIntegration({
                firmId: user.firmId,
                id: newCase.id,
                externalIntegration: tempCase.integration,
              });
            }
            if (newCase) {
              newCase.integration = tempCase.integration;
            }
            // Get external clients
            try {
              await fetchExternalClients();
            } catch (error) {
              setFormError(ERROR_MSG.EXTERNAL_PARTNER.GENERAL);
              rg4js("send", {
                error: normalizeError(error),
              });
              setSaving(false);
              return;
            }
          }

          if (newCase) {
            tempCase = newCase;
          }
          document.clientId = null;
        } catch (error) {
          setFormError(ERROR_MSG.GENERAL);
          rg4js("send", {
            error: normalizeError(error),
          });
          setSaving(false);
          return;
        }
      } else {
        // If the current case already exists
        if (_case.integration?.identifier) {
          if (!jurisdictionId) throw new Error("jurisdictionId is undefined");
          if (!determineVenue) throw new Error("determineVenue is undefined");

          try {
            if (
              _case.jurisdiction === blankId ||
              _case.venue === blankId ||
              jurisdictionId !== _case?.jurisdiction
            ) {
              tempCase.jurisdiction = jurisdictionId;
              tempCase.venue = determineVenue;
              try {
                if (!tempCase.id) throw new Error("tempCase.id is undefined");

                await caseApi.casePut({
                  firmId: user.firmId,
                  id: tempCase.id,
                  _case: tempCase,
                });
              } catch (error) {
                setFormError(ERROR_MSG.GENERAL);
                rg4js("send", {
                  error: normalizeError(error),
                });
                setSaving(false);
              }
            }
            const res = (await externalRequestWrapper(
              clientApi.clientGetExternal({
                firmId: user.firmId,
                externalCaseId: tempCase.integration?.identifier || undefined,
              }),
              3,
            )) as ExternalClientIEnumerableExternalPartnerResponseResponse;
            handleExternalReqResCodes(
              res,
              exClientFetchSuccess,
              exClientFetchError,
              initiateAuth,
            );
          } catch (error) {
            setFormError(ERROR_MSG.GENERAL);
            rg4js("send", {
              error: normalizeError(error),
            });
            setSaving(false);
            return;
          }
        }

        if (
          (!_case.caseNumber && caseNumber) ||
          (!_case.judge && judge) ||
          (!_case.caseType && caseType)
        ) {
          tempCase.caseNumber ??= caseNumber;
          tempCase.judge ??= judge;
          tempCase.caseType ??= caseType;
          try {
            if (!tempCase.id) throw new Error("tempCase.id is undefined");

            await caseApi.casePut({
              firmId: user.firmId,
              id: tempCase.id,
              _case: tempCase,
            });
          } catch {
            setFormError(ERROR_MSG.GENERAL);
            setSaving(false);
            return;
          }
        }
        document.clientId = tempCase.clientId;
      }

      if (!client?.id) {
        // Create a client ID if none exists
        if (client?.aliases) {
          client.aliases = pleadClientPos?.name;
        }

        try {
          const clientName = clientIsDefendant
            ? setDefendantName
            : setPlaintiffName;

          const newClient = (await clientApi.clientPost({
            firmId: user.firmId,
            client: { name: clientName },
          })) as ClientResponse;
          tempClient = newClient;
        } catch {
          setFormError(ERROR_MSG.GENERAL);
          setSaving(false);
          return;
        }
      }

      if (!document.clientId || tempClient.id !== document.clientId) {
        document.clientId = tempClient.id;
        tempCase.clientId ||= tempClient.id;
      }

      // Adds client to case
      try {
        if (!tempCase.id) throw new Error("tempCase.id is undefined");

        await caseApi.caseAddClient({
          firmId: user.firmId,
          id: tempCase.id,
          body: tempClient.id,
        });
      } catch {
        setFormError(ERROR_MSG.GENERAL);
        setSaving(false);
        return;
      }

      if (
        (!tempCase.clientPosition ||
          tempCase.clientPosition === PartyPosition.Unknown) &&
        pleadClientPos?.clientPos
      ) {
        tempCase.clientPosition = GetEnumValues(pleadClientPos.clientPos).join(
          ", ",
        ) as PartyPosition;
        try {
          await caseApi.casePut({
            firmId: user.firmId,
            id: tempCase.id,
            _case: tempCase,
          });
        } catch {
          setFormError(ERROR_MSG.GENERAL);
          setSaving(false);
          return;
        }
      }

      if (fuzzyOtherPartyResults && fuzzyOtherPartyResults.length > 0) {
        setFoundFuzzyPartyId(fuzzyOtherPartyResults[0].item.id);
      } else {
        try {
          const otherName = !clientIsDefendant
            ? setDefendantName
            : setPlaintiffName;

          await caseApi.caseCreateParty({
            firmId: user.firmId,
            id: tempCase.id,
            party: {
              name: otherName,
              position: clientIsDefendant
                ? PartyPosition.Plaintiff
                : PartyPosition.Defendant,
            },
          });
        } catch {
          setFormError(ERROR_MSG.GENERAL);
          setSaving(false);
          return;
        }
      }

      try {
        await caseApi.casePut({
          firmId: user.firmId,
          id: tempCase.id,
          _case: tempCase,
        });
      } catch {
        setFormError(ERROR_MSG.GENERAL);
        setSaving(false);
        return;
      }

      document.caseId = tempCase.id;
      if (document.info) {
        document.info.venue = determineVenue;
        document.info.jurisdiction = jurisdictionId;
      }
      if (await handleUpdateDocument()) {
        // If the user is using the template propound flow, we need to redirect to the case page
        if (documentSubTypeTemplate) {
          history.push(
            `/cases/${tempCase.id}?generate=${documentSubTypeTemplate}`,
          );
        } else {
          setCase(tempCase);
          setSection("page2Pd");
          setConfirmedSections(["page1Pd"]);
        }
      }

      if (jurisdictionId && firm && firmApi) {
        //if user has selected federal and they don't have it in their office add it to the firm
        //in the future we may want to do this for other jurisdictions as well
        await checkAndAddJurisdictionToOffice(jurisdictionId, firm, firmApi);
      }
    } else if (section === "page2Pd") {
      const tempCase = {
        ..._case,
        damages,
        incidentTime,
        incidentDate,
        incidentLocation,
        incidentDescription,
        timeline: timelineDetails,
        causesOfAction,
        agreements,
        injuries,
        genericInfo,
      };

      if (!tempCase.id) throw new Error("tempCase.id is undefined");

      try {
        await caseApi.casePut({
          firmId: user.firmId,
          id: tempCase.id,
          _case: tempCase,
        });
      } catch {
        setFormError(ERROR_MSG.GENERAL);
        setSaving(false);
        return;
      }

      if (await handleUpdateDocument()) {
        setSection("page3Pd");
        setConfirmedSections(["page1Pd", "page2Pd"]);
      }
    } else {
      const tempCase = _case;
      const matchingOtherParty = tempCase?.otherParties?.find(
        (p) => p.name === respondingPartyName,
      );
      const resId = _case?.otherParties?.[0].id ?? undefined;

      if (!document.info) throw new Error("document.info is undefined");

      document.info.setNumber = setNumber;
      document.info.startingNumber = startingNumber;

      if (!tempCase) throw new Error("tempCase is undefined");

      tempCase.allegations = allegations;

      if (document.info?.caseMetadata) {
        document.info.caseMetadata.allegations = allegations;
      }

      try {
        if (!tempCase.id) throw new Error("tempCase.id is undefined");

        await caseApi.casePut({
          firmId: user.firmId,
          id: tempCase.id,
          _case: tempCase,
        });
      } catch {
        setFormError(ERROR_MSG.GENERAL);
        setSaving(false);
        return;
      }

      const createDiscoveryRequest: CreateDiscoveryRequest = {
        caseId: _case.id,
        clientId: client?.id,
        setNumber: setNumber,
        respondingId: foundFuzzyPartyId
          ? foundFuzzyPartyId
          : matchingOtherParty
            ? matchingOtherParty.id
            : resId,
        questions: null,
        parentDocumentId: document.id,
      };

      if (selectedRequestTypes.length > 0) {
        for (let i = 0; i < selectedRequestTypes.length; i++) {
          const t = selectedRequestTypes[i];

          createDiscoveryRequest.documentSubType = t.subtype;
          createDiscoveryRequest.startingNumber =
            (setNumber ?? 1) > 1 ? t.number : undefined;
          try {
            await documentApi.documentStartGenerateRequests({
              createDiscoveryRequest,
            });
          } catch {
            setFormError(ERROR_MSG.GENERAL);
            setSaving(false);
            return;
          }
        }

        setGeneratingRequests(true);
      }

      document.status = DocumentStatus.InfoConfirmed;
      if (await handleUpdateDocument()) {
        setCase(tempCase);
        if (selectedRequestTypes.length === 0) window.location.href = "/";
      }
      setSection("page3Pd");
      setConfirmedSections(["page1Pd", "page2Pd", "page3Pd"]);
    }
    setSaving(false);
    return;
  }

  async function onSubmit(
    autoObjectAfter = false,
    documentSubType?: DocumentSubType,
  ) {
    setSaving(true);
    setAutoObject(autoObjectAfter);
    // Take to pleadings SDR flow if its a pleading/complain, else, go to regular sdr flow
    if (documentType === DocumentType.Pleading) {
      sdrPleadingUiFlow(documentSubType);
    } else {
      sdrUiFlow(autoObjectAfter);
    }
  }

  // TODO: types-wise, this is a nightmare. Refactor.
  function onCaseUpdate(
    c: Case | undefined | ((prevState: Case | undefined) => Case | undefined),
  ) {
    setCase(c);
    if (typeof c === "function") {
      return;
    }
    if (!c) {
      return;
    }
    // new cases don't have an id
    if (c.id) {
      if (c.jurisdiction !== blankId) {
        setJurisdictionId(c.jurisdiction);
      }
    }
  }

  //federal should always be available so add it to the list of jx
  const officeJurisdictions = firm?.offices?.flatMap((o) => {
    return o.jurisdictions?.map((j) => j.jurisdictionId ?? "") ?? [];
  }) ?? [document.info?.jurisdiction ?? CaliforniaId];
  if (!officeJurisdictions.find((of) => of === FederalId)) {
    officeJurisdictions.push(FederalId);
  }

  const isConfirmDisabled = !_case || !venue || venue === blankId || saving;

  return (
    <Container className="page-container">
      <header className="mb-3">
        <h1
          data-testid="source-document-review-title"
          className={styles.pageTitle}
        >
          Source Document Review
        </h1>
        <p className={styles.pageSubtitle}>
          Let's quickly confirm key pieces of data before we get started.
        </p>
      </header>
      {formError && <Alert variant="destructive">{formError}</Alert>}
      <Row>
        <Col xs="6">
          <div>
            <PdfViewer documentId={document.id} height={830} width={740} />
          </div>
        </Col>
        <Col xs="5">
          <div className={styles.confirmInformationContainerWrapper}>
            <div className={styles.confirmInformationContainer}>
              <SourceDocumentReviewSteps
                numSteps={sections.length}
                activeIndex={confirmedSections.length}
                completedStepIndexes={completedSteps}
              />
              {!saving && generatingRequests ? (
                <>
                  {/*Add Animated Gif Here once its finished*/}
                  <div className={styles.pleadingSuccess}>
                    {/* biome-ignore lint/a11y/noSvgWithoutTitle: TODO: add title, move to separate file */}
                    <svg
                      style={{ marginTop: "-2px" }}
                      width="16"
                      height="17"
                      viewBox="0 0 16 17"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <g clipPath="url(#clip0_15_3207)">
                        <path
                          d="M0.208008 6.18145C0.208008 6.18145 2.23034 6.06688 3.56788 4.43251C4.90543 2.79814 5.36195 0.0146484 5.36195 0.0146484C5.36195 0.0146484 5.8385 2.78803 7.05991 4.40555C8.28132 6.02307 10.3918 6.18482 10.3918 6.18482C10.3918 6.18482 8.39746 6.33984 7.12399 7.8967C5.85052 9.45357 5.37797 11.9473 5.37797 11.9473C5.37797 11.9473 4.89742 9.58499 3.50782 7.85964C2.2784 6.32636 0.208008 6.18145 0.208008 6.18145Z"
                          fill="currentColor"
                        >
                          <animate
                            attributeName="opacity"
                            values=".2;1;.2"
                            dur="2.5s"
                            begin="-2s"
                            repeatCount="indefinite"
                          />
                        </path>
                        <path
                          d="M7.09619 11.8428C7.09619 11.8428 8.55788 11.7619 9.527 10.5791C10.4961 9.39629 10.8245 7.38449 10.8245 7.38449C10.8245 7.38449 11.1689 9.38955 12.0539 10.5589C12.9389 11.7282 14.4647 11.8462 14.4647 11.8462C14.4647 11.8462 13.023 11.9574 12.102 13.0829C11.1809 14.2084 10.8405 16.0113 10.8405 16.0113C10.8405 16.0113 10.4921 14.3028 9.48695 13.0559C8.59392 11.9473 7.1002 11.8428 7.1002 11.8428H7.09619Z"
                          fill="currentColor"
                        >
                          <animate
                            attributeName="opacity"
                            repeatCount="indefinite"
                          />
                        </path>
                        <path
                          d="M9.4669 3.73833C9.4669 3.73833 10.5802 3.6743 11.317 2.77792C12.0539 1.88154 12.3022 0.348267 12.3022 0.348267C12.3022 0.348267 12.5625 1.8748 13.2352 2.76107C13.908 3.64734 15.0654 3.7417 15.0654 3.7417C15.0654 3.7417 13.9681 3.82594 13.2673 4.68188C12.5665 5.53782 12.3062 6.90935 12.3062 6.90935C12.3062 6.90935 12.0419 5.60859 11.277 4.66166C10.6002 3.8192 9.46289 3.73833 9.46289 3.73833H9.4669Z"
                          fill="currentColor"
                        >
                          <animate
                            attributeName="opacity"
                            values=".2;1;.2"
                            dur="2.5s"
                            repeatCount="indefinite"
                          />
                        </path>
                      </g>
                      <defs>
                        <clipPath id="clip0_15_3207">
                          <rect
                            width="14.8571"
                            height="16"
                            fill="white"
                            transform="translate(0.208008 0.0146484)"
                          />
                        </clipPath>
                      </defs>
                    </svg>
                    <p>Creating Tailored Discovery... </p>
                    <p>
                      This may take a few moments. If you navigate away from
                      this page, you will be able to access your discovery from
                      the{" "}
                      <Link to={`/?highlight=${document.id}`}>
                        Document List
                      </Link>
                      .
                    </p>
                  </div>
                </>
              ) : (
                <FormPreventDefault onSubmit={() => onSubmit()}>
                  {/*
                   * This is the "Atlas Component", which holds up the world.
                   * TODO: refactor into smaller bits. This is too complex.
                   * No component should have more than handful of props.
                   */}
                  <SectionComponent
                    docJurisdiction={document.info?.jurisdiction}
                    jurisdiction={jurisdictions.find(
                      (j) => j.id === (jurisdictionId ?? CaliforniaId),
                    )}
                    jurisdictions={jurisdictions}
                    docVenue={document.info?.venue || ""}
                    venue={venue}
                    setVenue={setVenue}
                    setJurisdiction={setJurisdictionId}
                    officeJurisdictions={officeJurisdictions}
                    caseNumber={caseNumber}
                    setCaseNumber={setCaseNumber}
                    caseMetaData={caseMetaData}
                    setCaseMetaData={setCaseMetaData}
                    documentType={documentType}
                    communication={communication}
                    documentSubType={documentSubType}
                    setDocumentSubType={setDocumentSubType}
                    interrogatories={document.interrogatories}
                    propoundingParty={propoundingParty}
                    setPropoundingParty={setPropoundingParty}
                    respondingParty={respondingParty}
                    setRespondingParty={setRespondingParty}
                    docPropoundingName={document.info?.propoundingPartyName}
                    propoundingPartyName={propoundingPartyName}
                    setPropoundingPartyName={setPropoundingPartyName}
                    respondingPartyName={respondingPartyName}
                    setRespondingPartyName={setRespondingPartyName}
                    damages={damages}
                    firm={firm}
                    caseType={caseType}
                    setCaseType={setCaseType}
                    pleadClientPos={pleadClientPos}
                    setPleadClientPos={setPleadClientPos}
                    setDamages={setDamages}
                    setClientIsDefendant={setClientIsDefendant}
                    clientIsDefendant={clientIsDefendant}
                    incidentDate={incidentDate}
                    setIncidentDate={setIncidentDate}
                    incidentDescription={incidentDescription}
                    setIncidentDescription={setIncidentDescription}
                    incidentLocation={incidentLocation}
                    setIncidentLocation={setIncidentLocation}
                    incidentTime={incidentTime}
                    setIncidentTime={setIncidentTime}
                    isCrossComplaint={isCrossComplaint}
                    causesOfAction={causesOfAction}
                    setCausesOfAction={setCausesOfAction}
                    selectedRequestTypes={selectedRequestTypes}
                    setSelectedRequestTypes={setSelectedRequestTypes}
                    genericInfo={genericInfo}
                    setGenericInfo={setGenericInfo}
                    timelineDetails={timelineDetails}
                    setTimelineDetails={setTimelineDetails}
                    allegations={allegations}
                    setAllegations={setAllegations}
                    injuries={injuries}
                    setInjuries={setInjuries}
                    agreements={agreements}
                    setAgreements={setAgreements}
                    judge={judge}
                    setJudge={setJudge}
                    caseId={document.caseId || undefined}
                    showEdit={showEdit}
                    setShowEdit={setShowEdit}
                    _case={_case || undefined}
                    setCase={onCaseUpdate}
                    client={client || undefined}
                    setClient={setClient}
                    cases={cases}
                    externalCases={externalCases}
                    filterExternalCases={handleFilterChange}
                    clients={clients}
                    areThereFuzzyClientResults={areThereFuzzyClientResults}
                    externalClients={externalClients}
                    dateOfService={dateOfService}
                    setDateOfService={setDateOfService}
                    methodOfService={methodOfService}
                    setMethodOfService={setMethodOfService}
                    dueDate={dueDate}
                    setDueDate={setDueDate}
                    respondent={respondent}
                    setRespondent={updateRespondent}
                    respondentCustom={respondentCustom}
                    setRespondentCustom={setRespondentCustom}
                    setNumber={setNumber}
                    setSetNumber={setSetNumber}
                    startingNumber={startingNumber}
                    setStartingNumber={setStartingNumber}
                    isExternalCasesLoading={isExternalCasesLoading}
                  />
                  <div className="d-flex justify-content-end mt-3 gap-2">
                    {section === "page1" &&
                      document.caseId &&
                      (showEdit ? (
                        <Button variant="link" onClick={cancelShowEdit}>
                          Cancel
                        </Button>
                      ) : null)}
                    {section === "page1Pd" ? (
                      <div className={styles.propoundingDropdownWrapper}>
                        {!saving && (
                          <Dropdown>
                            <Dropdown.Toggle
                              as={BootstrapButton}
                              data-testid="source-document-review-confirm-and-propound-button"
                              disabled={isConfirmDisabled}
                              variant="outline"
                              className={`${styles.draftDropdownButton} ${styles.propoundingButton}`}
                            >
                              {saving && !autoObject ? (
                                <Loading isLoading={true} text="Saving..." />
                              ) : (
                                "Standard Propounding"
                              )}
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                              <Dropdown.Item
                                onClick={() =>
                                  onSubmit(
                                    false,
                                    DocumentSubType.RequestsForAdmission,
                                  )
                                }
                                disabled={isConfirmDisabled}
                              >
                                {
                                  getSubTypeText(
                                    DocumentSubType.RequestsForAdmission,
                                    _case?.jurisdiction ?? "",
                                  ).plural
                                }
                              </Dropdown.Item>
                              <Dropdown.Item
                                onClick={() =>
                                  onSubmit(
                                    false,
                                    DocumentSubType.RequestsForProduction,
                                  )
                                }
                                disabled={isConfirmDisabled}
                              >
                                {
                                  getSubTypeText(
                                    DocumentSubType.RequestsForProduction,
                                    _case?.jurisdiction ?? "",
                                  ).plural
                                }
                              </Dropdown.Item>
                              <Dropdown.Item
                                onClick={() =>
                                  onSubmit(
                                    false,
                                    DocumentSubType.SpecialInterrogatories,
                                  )
                                }
                                disabled={isConfirmDisabled}
                              >
                                {
                                  getSubTypeText(
                                    DocumentSubType.SpecialInterrogatories,
                                    _case?.jurisdiction ?? "",
                                  ).plural
                                }
                              </Dropdown.Item>
                            </Dropdown.Menu>
                          </Dropdown>
                        )}
                        <Button
                          data-testid="source-document-review-confirm-button"
                          loading={saving && !autoObject}
                          disabled={isConfirmDisabled}
                          text="Saving..."
                          type="submit"
                          className={styles.propoundingButton}
                        >
                          AI-Generated Propounding
                        </Button>
                      </div>
                    ) : (
                      <>
                        {section === "page3" &&
                          useAutoObject &&
                          documentSubType !==
                            DocumentSubType.FormInterrogatories && (
                            <Button
                              variant="outline"
                              disabled={isConfirmDisabled}
                              text="Objecting..."
                              onClick={() => onSubmit(true)}
                            >
                              <Sparkle sparkling={saving && autoObject} />
                              Confirm and Auto Object
                            </Button>
                          )}
                        <Button
                          data-testid="source-document-review-confirm-button"
                          variant="default"
                          loading={saving && !autoObject}
                          disabled={isConfirmDisabled}
                          text="Saving..."
                          type="submit"
                        >
                          Confirm
                        </Button>
                      </>
                    )}
                  </div>
                </FormPreventDefault>
              )}
            </div>
          </div>
        </Col>
      </Row>
    </Container>
  );
}
